import {
  MentionTag,
  MetadataTag,
  TagContainer,
  ThemeTag,
} from '@/features/analyze/components/Tag';
import {
  BuildingOffice2Icon,
  CubeIcon,
  GlobeAltIcon,
  HashtagIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import {
  CollapsibleTagList,
  BookmarkButton,
  ExpandableContent,
  ExternalLink,
} from '@/components/Elements';
import { type ActiveArticle } from '@/features/analyze/hooks/useAnalyzeClustering';
import { TopicTagWithTooltip } from '@/features/analyze/components/TopicTagWithTooltip';

import { useSearchResultTagFilter } from '@/features/analyze/hooks/useSearchResultTagFilter';
import { type FacetKey } from '@/features/analyze/types';

type FoamTreeSelectItemProps = {
  selectedArticle: ActiveArticle;
};

export const FoamTreeSelectItem = ({
  selectedArticle,
}: FoamTreeSelectItemProps) => {
  const { t } = useTranslation();
  const addSearchResultTagFilter = useSearchResultTagFilter();

  if (!selectedArticle) return <></>;

  const onClick = (targetFieldKey: FacetKey, clickText: string | null) => {
    if (clickText == null) return;
    addSearchResultTagFilter(targetFieldKey, clickText);
  };

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-start justify-between gap-x-3">
        <h4 className="min-w-0 flex-1 text-xl font-semibold">
          <ExternalLink to={selectedArticle.url}>
            {selectedArticle.title}
          </ExternalLink>
        </h4>
        <div className="mt-1 shrink-0">
          <BookmarkButton
            key={selectedArticle.code}
            code={selectedArticle.code}
            title={selectedArticle.title}
            url={selectedArticle.url}
            isBookmarked={selectedArticle.isBookmarkedByCurrentUser}
            className="p-0"
          />
        </div>
      </div>
      <TagContainer>
        {selectedArticle.themes && selectedArticle.themes.length > 0 && (
          <CollapsibleTagList
            items={selectedArticle.themes}
            maxVisibleItems={3}
            textColor="text-rw-sub"
            renderTag={(theme: string) => (
              <ThemeTag
                key={theme}
                icon={HashtagIcon}
                tableName={theme}
                onClick={() => addSearchResultTagFilter('themes', theme)}
              />
            )}
          />
        )}
        {selectedArticle.country && (
          <MetadataTag
            icon={GlobeAltIcon}
            text={selectedArticle.country}
            onClick={() => onClick('countries', selectedArticle.country)}
          />
        )}
        {selectedArticle.organizationType && (
          <MetadataTag
            icon={CubeIcon}
            text={selectedArticle.organizationType}
            onClick={() =>
              onClick('organizationTypes', selectedArticle.organizationType)
            }
          />
        )}
        {selectedArticle.organization && (
          <MetadataTag
            icon={BuildingOffice2Icon}
            text={selectedArticle.organization}
            onClick={() =>
              onClick('organizations', selectedArticle.organization)
            }
          />
        )}
        {selectedArticle.displayableTopicTags &&
          selectedArticle.displayableTopicTags.length > 0 && (
            <CollapsibleTagList
              items={selectedArticle.displayableTopicTags}
              maxVisibleItems={10}
              textColor="text-rw-black"
              renderTag={(tag) => (
                <TopicTagWithTooltip
                  key={tag.lookupKey}
                  name={tag.name}
                  description={tag.description}
                  themes={tag.themes}
                  isSocialIssue={tag.isSocialIssue}
                  icon={TagIcon}
                  onClick={() => onClick('topicTags', tag.lookupKey)}
                />
              )}
            />
          )}
      </TagContainer>
      <ExpandableContent aria-label="記事サマリ" className="line-clamp-6">
        {`${new Date(selectedArticle.publishedAt).toLocaleDateString()} - ${selectedArticle.summary ?? ''}`}
      </ExpandableContent>

      <hr />

      <TagContainer title={t('言及された人物')}>
        {selectedArticle.people?.map((person) => (
          <MentionTag key={person} text={person} />
        )) ?? '-'}
      </TagContainer>
      <TagContainer title={t('言及された組織')}>
        {selectedArticle.companies?.map((company) => (
          <MentionTag key={company} text={company} />
        )) ?? '-'}
      </TagContainer>
      <TagContainer title={t('言及された地名')}>
        {selectedArticle.locations?.map((location) => (
          <MentionTag key={location} text={location} />
        )) ?? '-'}
      </TagContainer>
    </div>
  );
};
